import React, {useContext} from "react"

import {useState} from "react"
import {Button} from "@/components/shadcn/ui/button"
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/components/shadcn/ui/card"
import {RadioGroup, RadioGroupItem} from "@/components/shadcn/ui/radio-group"
import {Label} from "@/components/shadcn/ui/label"
import {Textarea} from "@/components/shadcn/ui/textarea"
import {CheckCircle2, X} from "lucide-react"
import {Separator} from "@/components/shadcn/ui/separator"
import Image from "next/image";
import {useRouter} from "next/router";
import {GoogleReCaptchaProvider, useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {Dictionary} from "@/types/hygiena-types";
import {DictionaryContext} from "@/context/dictionary-context";

/**
 * Creates the Feedback Form component wrapped in the recaptcha provider.
 * @constructor
 */
export default function FeedbackForm() {
  try {
    return (
      <>
        {process?.env?.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.length && (
          <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY} scriptProps={{
            async: true,
            defer: true,
            appendTo: "body",
            nonce: undefined,
            id: "site_feedback",
          }}>
            <FeedbackFormComponent/>
          </GoogleReCaptchaProvider>
        )}
      </>
    );
  } catch (error) {
    return <></>
  }
}

/**
 * The feedback form component.
 * @constructor
 */
function FeedbackFormComponent() {
  const t = useContext<Dictionary>(DictionaryContext);
  const {asPath, locale, defaultLocale} = useRouter();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [completed, setCompleted] = useState(false)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [textFeedback, setTextFeedback] = useState("")
  const [openForm, setOpenForm] = useState(false)
  const [finalize, setFinalize] = useState<boolean>(false)
  const {executeRecaptcha} = useGoogleReCaptcha();

  if (!t?.feedback) return <></>

// Define the questions and their options
  const questions = [
    {
      id: 1,
      title: t.feedback.questions['1']['title'],
      question: t.feedback.questions['1']['question'],
      type: "multiple-choice",
      options: [
        {value: "5", label: t.feedback.questions['1']['values']['5']},
        {value: "4", label: t.feedback.questions['1']['values']['4']},
        {value: "3", label: t.feedback.questions['1']['values']['3']},
        {value: "2", label: t.feedback.questions['1']['values']['2']},
        {value: "1", label: t.feedback.questions['1']['values']['1']},
        {value: "0", label: t.feedback.questions['1']['values']['0'], separator: true},
      ],
    },
    {
      id: 2,
      title: t.feedback.questions['2']['title'],
      question: t.feedback.questions['2']['question'],
      type: "multiple-choice",
      options: [
        {value: "5", label: t.feedback.questions['2']['values']['5']},
        {value: "4", label: t.feedback.questions['2']['values']['4']},
        {value: "3", label: t.feedback.questions['2']['values']['3']},
        {value: "2", label: t.feedback.questions['2']['values']['2']},
        {value: "1", label: t.feedback.questions['2']['values']['1']},
        {value: "0", label: t.feedback.questions['2']['values']['0'], separator: true},
      ],
    },
    {
      id: 3,
      title: t.feedback.questions['3']['title'],
      question: t.feedback.questions['3']['question'],
      type: "multiple-choice",
      options: [
        {value: "5", label: t.feedback.questions['3']['values']['5']},
        {value: "4", label: t.feedback.questions['3']['values']['4']},
        {value: "3", label: t.feedback.questions['3']['values']['3']},
        {value: "2", label: t.feedback.questions['3']['values']['2']},
        {value: "1", label: t.feedback.questions['3']['values']['1']},
        {value: "0", label: t.feedback.questions['3']['values']['0'], separator: true},
      ],
    },
    {
      id: 4,
      title: t.feedback.questions['4']['title'],
      question: t.feedback.questions['4']['question'],
      type: "multiple-choice",
      options: [
        {value: "5", label: t.feedback.questions['4']['values']['5']},
        {value: "4", label: t.feedback.questions['4']['values']['4']},
        {value: "3", label: t.feedback.questions['4']['values']['3']},
        {value: "2", label: t.feedback.questions['4']['values']['2']},
        {value: "1", label: t.feedback.questions['4']['values']['1']},
        {value: "0", label: t.feedback.questions['4']['values']['0'], separator: true},
      ],
    },
    {
      id: 5,
      title: t.feedback.questions['5']['title'],
      question: t.feedback.questions['5']['question'],
      type: "text",
    },
  ]


  const handleOptionSelect = (value: string) => {
    setSelectedOption(value)
  }

  const handleNext = () => {
    const currentQuestion = questions[currentQuestionIndex]

    if (currentQuestion.type === "multiple-choice" && selectedOption) {
      // Save the current multiple-choice answer
      setAnswers({
        ...answers,
        [currentQuestion.id]: selectedOption,
      })

      moveToNextQuestion()
    } else if (currentQuestion.type === "text") {
      // Save the text feedback
      setAnswers({
        ...answers,
        [currentQuestion.id]: textFeedback,
      })

      moveToNextQuestion()
    }
  }

  const moveToNextQuestion = () => {
    // Check if this is the last question
    if (currentQuestionIndex === questions.length - 1) {
      const body = {
        form_id: 'site_feedback',
        ease_of_finding_information: answers[1],
        clarity_usefulness_of_content: answers[2],
        overall_design_layout: answers[3],
        recommendation_likelihood: answers[4],
        further_feedback: textFeedback,
        web_page: locale !== defaultLocale ? `/${locale}${asPath}` : asPath,
        recaptcha_token: '',
      };
      if (executeRecaptcha) {
        executeRecaptcha().then(token => {
          body.recaptcha_token = token;
          fetch(`/api/webform-submit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': `${locale}`
            },
            body: JSON.stringify(body),
          }).then(async (res) => {
            setCompleted(true)
          });
        });
      }
    } else {
      // Move to the next question
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setSelectedOption(null)
    }
  }

  const handleRestart = () => {
    setCurrentQuestionIndex(0)
    setAnswers({})
    setCompleted(false)
    setSelectedOption(null)
    setTextFeedback("")
  }

  const buttonStyle = {
    backgroundColor: "#00B0F0",
    color: "white",
    "--tw-ring-color": "rgba(0, 176, 240, 0.5)",
  } as React.CSSProperties

  if (completed && !finalize) {
    return (
      <Card className="w-full max-w-md mx-auto fixed right-0 bottom-0 z-[9999] max-lg:hidden">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="h-16 w-16 text-green-500"/>
          </div>
          <CardTitle className="text-2xl">{t.feedback.complete['thanks']}</CardTitle>
          <CardDescription>{t.feedback.complete['description']}</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button onClick={() => setFinalize(true)} className="w-full hover:bg-opacity-90 transition-colors"
                  style={buttonStyle}>
            {t.feedback.form['button']['close']}
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]
  const isTextQuestion = currentQuestion.type === "text"
  const isButtonDisabled = isTextQuestion ? false : !selectedOption

  if (openForm && !finalize) {
    return (
      <Card className="w-full max-w-md mx-auto fixed right-2 bottom-2 z-[9999] max-lg:hidden">
        <CardHeader>
          <button className="flex justify-end mb-4 text-primary" onClick={() => {
            handleRestart();
            setOpenForm(false)
          }}><X/></button>
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold">{t.feedback.form['title']}</h2>
            <p className="text-muted-foreground mt-1">
              {t.feedback.form['description']}
            </p>
          </div>
          <div className="flex justify-end mb-1">
          <span className="text-sm text-muted-foreground">
            {t.feedback['form']['question']} {currentQuestionIndex + 1}{t.feedback['form']['of']}{questions.length}
          </span>
          </div>
          <CardTitle className="mb-2 text-base">{currentQuestion.title}</CardTitle>
          <CardDescription>{currentQuestion.question}</CardDescription>
        </CardHeader>
        <CardContent>
          {isTextQuestion ? (
            <Textarea
              placeholder={t.feedback.form['placeholder']}
              className="min-h-[120px]"
              value={textFeedback}
              onChange={(e) => setTextFeedback(e.target.value)}
            />
          ) : (
            <RadioGroup value={selectedOption || ""} onValueChange={handleOptionSelect} className="space-y-3">
              {currentQuestion.options?.map((option, index, array) => (
                <div key={option.value}>
                  {option.separator && <Separator className="my-3"/>}
                  <div
                    className={`flex items-center space-x-2 rounded-md border p-3 cursor-pointer transition-colors ${
                      selectedOption === option.value ? "bg-primary/10 border-primary" : "hover:bg-muted"
                    }`}
                    onClick={() => handleOptionSelect(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={`option-${option.value}`}/>
                    <Label htmlFor={`option-${option.value}`} className="flex-1 cursor-pointer font-medium">
                      {option.label}
                    </Label>
                  </div>
                </div>
              ))}
            </RadioGroup>
          )}
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleNext}
            disabled={isButtonDisabled}
            className="w-full hover:bg-opacity-90 transition-colors"
            style={buttonStyle}
          >
            {currentQuestionIndex === questions.length - 1 ? t.feedback.form['button']['submit'] : t.feedback.form['button']['next']}
          </Button>
        </CardFooter>
      </Card>
    )
  }

  if (!finalize) {
    return (
      <button className="fixed right-2 bottom-2 z-[9999] hover:scale-125 transition-all duration-300 max-lg:hidden"
              title={t.feedback.form['icon']['alt']}
              onClick={() => setOpenForm(true)}>
        <Image src="/feedback-icon.svg" alt={t.feedback.form['icon']['alt']} width={50} height={50}/>
      </button>
    )
  }

  return <></>
}